import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { shuffleFeaturedItems } from '../../utils/featuredDrinkUtils';
import '../FeaturedItemsShowcase/FeaturedItemsShowcase.css';
import './FeaturedItemsModal.css';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const STORAGE = {
  lastClosedDate: 'featured_modal_last_closed_date',
  consecutiveCloses: 'featured_modal_consecutive_closes',
  /** Legacy key from consecutive-days logic — cleared on read. */
  legacyConsecutiveDays: 'featured_modal_consecutive_close_days',
  blockedUntil: 'featured_modal_blocked_until',
};

const AUTOPLAY_MS = 8000;
const SWIPE_THRESHOLD_PX = 40;
const CONSECUTIVE_CLOSES_BEFORE_SUGGESTION = 3;
const PAUSE_DAYS = 7;

const getTodayDate = () => new Date().toISOString().split('T')[0];

const readStorage = (key, fallback = null) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore quota / private mode
  }
};

const removeStorage = (...keys) => {
  try {
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

const isBlocked = () => {
  const blockedUntil = readStorage(STORAGE.blockedUntil);
  if (!blockedUntil) {
    return false;
  }

  const blockedDate = new Date(blockedUntil);
  if (Number.isNaN(blockedDate.getTime())) {
    removeStorage(STORAGE.blockedUntil);
    return false;
  }

  if (new Date() < blockedDate) {
    return true;
  }

  // Pause expired
  removeStorage(STORAGE.blockedUntil, STORAGE.consecutiveCloses, STORAGE.legacyConsecutiveDays);
  return false;
};

/**
 * Show rules:
 * - paused for 7 days → never
 * - closed today → once-per-day (hide until tomorrow)
 * - not closed today → show every time the menu is opened
 */
const shouldShowFeaturedModal = () => {
  if (isBlocked()) {
    return false;
  }

  const lastClosedDate = readStorage(STORAGE.lastClosedDate);
  return lastClosedDate !== getTodayDate();
};

const getConsecutiveCloses = () => {
  // Migrate away from old consecutive-days counter
  removeStorage(STORAGE.legacyConsecutiveDays);
  return parseInt(readStorage(STORAGE.consecutiveCloses, '0'), 10) || 0;
};

/** Count every explicit close; still mark today so the modal stays once-per-day. */
const recordExplicitClose = () => {
  const consecutive = getConsecutiveCloses() + 1;
  writeStorage(STORAGE.lastClosedDate, getTodayDate());
  writeStorage(STORAGE.consecutiveCloses, String(consecutive));
  return consecutive;
};

function FeaturedCard({ item, language, onItemClick, badgeLabel }) {
  return (
    <Box
      className="featured-showcase__card"
      role="listitem"
      onClick={() => onItemClick?.(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onItemClick?.(item);
        }
      }}
      tabIndex={onItemClick ? 0 : -1}
    >
      <Box className="featured-showcase__image-wrap">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="featured-showcase__image"
            draggable={false}
          />
        ) : (
          <Box className="featured-showcase__image featured-showcase__image--placeholder" />
        )}
        <Box className="featured-showcase__badge">{badgeLabel}</Box>
      </Box>

      <Box className="featured-showcase__body">
        <Typography component="h3" className="featured-showcase__name">
          {item.name}
        </Typography>

        {item.description ? (
          <Typography className="featured-showcase__description">
            {item.description}
          </Typography>
        ) : null}

        <Box className="featured-showcase__meta">
          {item.amount && item.unit ? (
            <Typography className="featured-showcase__amount">
              {`${Math.round(item.amount)} ${item.unit}`}
            </Typography>
          ) : (
            <span />
          )}

          <Box className="featured-showcase__prices">
            {item.priceLabel ? (
              <Typography className="featured-showcase__price">{item.priceLabel}</Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const FeaturedItemsModal = ({ items = [], enabled = false, onItemClick, language = 'en' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [displayItems, setDisplayItems] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartXRef = useRef(null);
  const autoplayRef = useRef(null);

  const hasItems = items.length > 0;
  const slideCount = displayItems.length;

  useEffect(() => {
    if (!enabled || !hasItems) {
      setOpen(false);
      return undefined;
    }

    if (!shouldShowFeaturedModal()) {
      return undefined;
    }

    // Not closed today → show on every menu visit
    const timer = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [enabled, hasItems]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDisplayItems(shuffleFeaturedItems(items));
    setCurrentSlide(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shuffle once per open
  }, [open]);

  const goTo = (direction) => {
    if (slideCount <= 1) {
      return;
    }

    setCurrentSlide((prev) => {
      if (direction === 'prev') {
        return (prev - 1 + slideCount) % slideCount;
      }
      return (prev + 1) % slideCount;
    });
  };

  useEffect(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }

    if (!open || slideCount <= 1) {
      return undefined;
    }

    autoplayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, AUTOPLAY_MS);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, [open, slideCount, currentSlide]);

  const finishClose = () => {
    setOpen(false);
    setCurrentSlide(0);
  };

  /** Explicit close (X / backdrop / View menu) — each close counts toward the streak. */
  const handleDismiss = () => {
    const consecutive = recordExplicitClose();

    if (consecutive >= CONSECUTIVE_CLOSES_BEFORE_SUGGESTION) {
      setOpen(false);
      setShowSuggestionModal(true);
      return;
    }

    finishClose();
  };

  /** Engaged with an item — hide for today, reset streak (positive action). */
  const handleItemClick = (item) => {
    writeStorage(STORAGE.lastClosedDate, getTodayDate());
    writeStorage(STORAGE.consecutiveCloses, '0');
    removeStorage(STORAGE.legacyConsecutiveDays);
    finishClose();
    onItemClick?.(item);
  };

  const handleKeepShowing = () => {
    writeStorage(STORAGE.consecutiveCloses, '0');
    removeStorage(STORAGE.legacyConsecutiveDays);
    setShowSuggestionModal(false);
    finishClose();
  };

  const handlePauseForWeek = () => {
    const until = new Date();
    until.setDate(until.getDate() + PAUSE_DAYS);
    writeStorage(STORAGE.blockedUntil, until.toISOString());
    writeStorage(STORAGE.consecutiveCloses, '0');
    removeStorage(STORAGE.legacyConsecutiveDays);
    setShowSuggestionModal(false);
    finishClose();
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.changedTouches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    touchStartXRef.current = null;

    if (startX == null || endX == null || slideCount <= 1) {
      return;
    }

    const delta = endX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) {
      return;
    }

    goTo(delta < 0 ? 'next' : 'prev');
  };

  if (!enabled || items.length === 0) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDismiss}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
        className="featured-items-modal"
        aria-labelledby="featured-items-modal-title"
        PaperProps={{
          sx: {
            overflow: 'hidden',
            maxHeight: '92dvh',
          },
        }}
      >
        <IconButton
          onClick={handleDismiss}
          className="featured-items-modal__close"
          aria-label={t('featuredModal.closeAria')}
        >
          <CloseIcon />
        </IconButton>

        <Box className="featured-items-modal__header">
          <Box className="featured-showcase__title-row">
            <AutoAwesomeIcon className="featured-showcase__icon" />
            <Typography id="featured-items-modal-title" component="h2" className="featured-showcase__title">
              {t('featuredModal.title')}
            </Typography>
          </Box>
          <Typography className="featured-showcase__subtitle">
            {t('featuredModal.subtitle')}
          </Typography>
        </Box>

        <DialogContent className="featured-items-modal__content">
          <Box
            className="featured-items-modal__slider-wrap"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <Box className="featured-items-modal__viewport">
              {slideCount > 1 && (
                <>
                  <IconButton
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      goTo('prev');
                    }}
                    className="featured-items-modal__nav featured-items-modal__nav--prev"
                    aria-label={t('featuredModal.prevAria')}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      goTo('next');
                    }}
                    className="featured-items-modal__nav featured-items-modal__nav--next"
                    aria-label={t('featuredModal.nextAria')}
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                  <Typography className="featured-items-modal__counter" aria-hidden="true">
                    {currentSlide + 1} / {slideCount}
                  </Typography>
                </>
              )}

              <Box
                className="featured-items-modal__track"
                style={{
                  width: `${Math.max(slideCount, 1) * 100}%`,
                  transform: `translateX(-${slideCount > 0 ? (currentSlide * 100) / slideCount : 0}%)`,
                }}
              >
                {displayItems.map((item) => (
                  <Box
                    key={`${item.drink_id}-${item.image || 'no-img'}`}
                    className="featured-items-modal__slide"
                    style={{ width: `${100 / Math.max(slideCount, 1)}%` }}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={item.name}
                  >
                    <FeaturedCard
                      item={item}
                      language={language}
                      onItemClick={handleItemClick}
                      badgeLabel={t('featuredModal.badge')}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {slideCount > 1 ? (
              <Box className="featured-items-modal__dots" role="tablist" aria-label="Featured items">
                {displayItems.map((item, index) => (
                  <button
                    key={`dot-${item.drink_id}`}
                    type="button"
                    className={`featured-items-modal__dot${index === currentSlide ? ' is-active' : ''}`}
                    aria-label={`${index + 1} / ${slideCount}`}
                    aria-current={index === currentSlide ? 'true' : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      setCurrentSlide(index);
                    }}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions className="featured-items-modal__actions">
          <Button onClick={handleDismiss} variant="outlined" className="featured-items-modal__btn">
            {t('featuredModal.exploreButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSuggestionModal}
        onClose={handleKeepShowing}
        maxWidth="sm"
        className="featured-items-modal__suggestion"
      >
        <DialogTitle className="featured-items-modal__suggestion-title">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('featuredModal.suggestionModal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2.5 }} className="featured-items-modal__suggestion-body">
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', lineHeight: 1.7 }}>
            {t('featuredModal.suggestionModal.message1')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: t('featuredModal.suggestionModal.message2') }}
          />
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1.25, p: 2.5 }}>
          <Button
            onClick={handlePauseForWeek}
            variant="contained"
            fullWidth
            className="featured-items-modal__suggestion-primary"
            sx={{ py: 1.4 }}
          >
            {t('featuredModal.suggestionModal.showLessButton')}
          </Button>
          <Button
            onClick={handleKeepShowing}
            variant="outlined"
            fullWidth
            className="featured-items-modal__suggestion-secondary"
            sx={{ py: 1.4 }}
          >
            {t('featuredModal.suggestionModal.keepShowingButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeaturedItemsModal;
